import { hermesClient } from "./hermes-singleton.js";
import type { SavedPfmMindmap } from "../local-storage.ts";

const REMOTE_CACHE_MARKER = "[PFM-MINDMAP-CACHE:v1]";
const REMOTE_CACHE_SCAN_LIMIT = 300;

type RemoteCacheEnvelope =
  | {
    cacheKey: string;
    savedAt: number;
    cleared: true;
  }
  | {
    cacheKey: string;
    savedAt: number;
    cleared?: false;
    mindmap: SavedPfmMindmap;
  };

function toJsonContent(envelope: RemoteCacheEnvelope): string {
  return `${REMOTE_CACHE_MARKER}\n${JSON.stringify(envelope)}`;
}

function parseEnvelope(content: string): RemoteCacheEnvelope | null {
  const raw = String(content || "").trim();
  if (!raw.startsWith(REMOTE_CACHE_MARKER)) return null;
  const jsonPart = raw.slice(REMOTE_CACHE_MARKER.length).trim();
  if (!jsonPart) return null;
  try {
    const parsed = JSON.parse(jsonPart) as Partial<RemoteCacheEnvelope>;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.cacheKey !== "string" || !parsed.cacheKey.trim()) return null;
    if (typeof parsed.savedAt !== "number") return null;
    if (parsed.cleared === true) {
      return {
        cacheKey: parsed.cacheKey,
        savedAt: parsed.savedAt,
        cleared: true,
      };
    }
    const mm = (parsed as { mindmap?: SavedPfmMindmap }).mindmap;
    if (!mm || typeof mm !== "object") return null;
    if (typeof mm.sessionKey !== "string" || !mm.sessionKey.trim()) return null;
    if (!Array.isArray(mm.nodes) || mm.nodes.length === 0) return null;
    return {
      cacheKey: parsed.cacheKey,
      savedAt: parsed.savedAt,
      mindmap: {
        sessionKey: mm.sessionKey,
        title: String(mm.title || ""),
        description: String(mm.description || ""),
        nodes: mm.nodes,
        savedAt: Number(mm.savedAt || parsed.savedAt),
      },
    };
  } catch {
    return null;
  }
}

async function readLatestMessages(
  sessionKey: string,
): Promise<Array<{ role?: string; content?: string }>> {
  const firstPage = await hermesClient.request<{
    messages?: Array<{ role?: string; content?: string }>;
    total?: number;
  }>("chat.history", { sessionKey, limit: 1, offset: 0 });
  const total = Number(firstPage?.total || firstPage?.messages?.length || 0);
  const offset = Math.max(0, total - REMOTE_CACHE_SCAN_LIMIT);
  const result = await hermesClient.request<{
    messages?: Array<{ role?: string; content?: string }>;
  }>("chat.history", { sessionKey, limit: REMOTE_CACHE_SCAN_LIMIT, offset });
  return result?.messages ?? [];
}

export async function persistPfmMindmapRemote(
  sessionKey: string,
  cacheKey: string,
  mindmap: SavedPfmMindmap,
): Promise<void> {
  const safeSessionKey = String(sessionKey || "").trim();
  const safeCacheKey = String(cacheKey || "").trim();
  if (!safeSessionKey || !safeCacheKey) return;
  if (!Array.isArray(mindmap.nodes) || mindmap.nodes.length === 0) return;
  const envelope: RemoteCacheEnvelope = {
    cacheKey: safeCacheKey,
    savedAt: Date.now(),
    mindmap,
  };
  await hermesClient.request("chat.inject", {
    sessionKey: safeSessionKey,
    role: "system",
    label: "pfm-mindmap-cache",
    content: toJsonContent(envelope),
  });
}

export async function clearPfmMindmapRemote(
  sessionKey: string,
  cacheKey: string,
): Promise<void> {
  const safeSessionKey = String(sessionKey || "").trim();
  const safeCacheKey = String(cacheKey || "").trim();
  if (!safeSessionKey || !safeCacheKey) return;
  const envelope: RemoteCacheEnvelope = {
    cacheKey: safeCacheKey,
    savedAt: Date.now(),
    cleared: true,
  };
  await hermesClient.request("chat.inject", {
    sessionKey: safeSessionKey,
    role: "system",
    label: "pfm-mindmap-cache-clear",
    content: toJsonContent(envelope),
  });
}

export async function loadPfmMindmapRemote(
  sessionKey: string,
  cacheKey: string,
): Promise<SavedPfmMindmap | null> {
  const safeSessionKey = String(sessionKey || "").trim();
  const safeCacheKey = String(cacheKey || "").trim();
  if (!safeSessionKey || !safeCacheKey) return null;
  const messages = await readLatestMessages(safeSessionKey);
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if ((msg.role || "") !== "system") continue;
    const envelope = parseEnvelope(String(msg.content || ""));
    if (!envelope) continue;
    if (envelope.cacheKey !== safeCacheKey) continue;
    if (envelope.cleared) return null;
    return envelope.mindmap;
  }
  return null;
}
